import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Space, Modal, Form, Input, message, Tag, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { userService, User } from '../services/userService';
import axios from '../utils/axios';
import dayjs from 'dayjs';
import { authService } from '../services/authService';

const { Option } = Select;

const UserManagement: React.FC = () => {
  const assignmentRequest = useRef(0);
  const [assignmentReady, setAssignmentReady] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // 获取当前用户信息
  const currentUser = authService.getCurrentUser();
  const isProjectAdmin = currentUser?.role === 'project_admin';
  const isSuperAdmin = currentUser?.role === 'super_admin';

  useEffect(() => {
    axios.get('/project-access').then(r => setProjects(r.data.data)).catch(() => message.error('获取项目失败'));
    fetchUsers();
  }, [isProjectAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAllUsers();
      setUsers(data);
    } catch (error) {
      message.error('获取用户列表失败');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<User> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '电话',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const roleMap: Record<string, { label: string; color: string }> = {
          'super_admin': { label: '超级管理员', color: 'blue' },
          'project_admin': { label: '项目经理', color: 'green' },
          audit_admin: { label: '审核员', color: 'orange' },
          readonly: { label: '只读人员', color: 'default' }
        };
        const roleInfo = roleMap[role] || { label: role, color: 'default' };
        return <Tag color={roleInfo.color}>{roleInfo.label}</Tag>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '正常' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '最后登录时间',
      dataIndex: 'lastLoginTime',
      key: 'lastLoginTime',
      render: (time: string) => dayjs(time).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          {isSuperAdmin && <Button
            icon={<KeyOutlined />}
            onClick={() => handleChangePassword(record)}
          >
            修改密码
          </Button>}
          {isSuperAdmin && <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>}
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    assignmentRequest.current++;
    setAssignmentReady(true);
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({ ...user, project_ids: [] });
    const request = ++assignmentRequest.current;
    setAssignmentReady(!['audit_admin', 'readonly'].includes(user.role));
    if (['audit_admin', 'readonly'].includes(user.role)) axios.get(`/project-access/users/${user.id}/projects`).then(r => { if (request === assignmentRequest.current) { form.setFieldsValue({ project_ids: r.data.data }); setAssignmentReady(true); } }).catch(() => message.error('读取项目指派失败，请关闭后重试'));
    setIsModalVisible(true);
  };

  const handleChangePassword = (user: User) => {
    setEditingUser(user);
    passwordForm.resetFields();
    setIsPasswordModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这个用户吗？',
      onOk: async () => {
        try {
          await userService.deleteUser(id);
          message.success('删除成功！');
          fetchUsers();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (!assignmentReady || saving) return;
      setSaving(true);
      if (editingUser) {
        await userService.updateUser(editingUser.id, values);
        message.success('更新成功！');
      } else {
        await userService.createUser(values);
        message.success('添加成功！');
      }
      setIsModalVisible(false);
      fetchUsers();
    } catch (error) {
      message.error((error as any).response?.data?.error || '操作失败');
    } finally { setSaving(false); }
  };

  const handlePasswordChange = async () => {
    try {
      const values = await passwordForm.validateFields();
      if (editingUser) {
        await userService.changePassword(editingUser.id, values.oldPassword, values.newPassword);
        message.success('密码修改成功！');
        setIsPasswordModalVisible(false);
      }
    } catch (error) {
      message.error('密码修改失败');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          添加用户
        </Button>
      </div>
      <Table 
        columns={columns} 
        dataSource={users} 
        rowKey="id" 
        loading={loading}
      />

      <Modal
        title={editingUser ? '编辑用户' : '添加用户'}
        open={isModalVisible}
        confirmLoading={saving}
        okButtonProps={{ disabled: !assignmentReady }}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="username"
            label="用户名"
            rules={[{ required: true, message: '请输入用户名！' }]}
          >
            <Input />
          </Form.Item>
          {!editingUser && (
            <Form.Item
              name="password"
              label="密码"
              rules={[{ required: true, message: '请输入密码！' }]}
            >
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名！' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱！' },
              { type: 'email', message: '请输入有效的邮箱地址！' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="电话"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色！' }]}
          >
            <Select placeholder="请选择角色">
              {isSuperAdmin && <Option value="super_admin">超级管理员</Option>}
              {isSuperAdmin && <Option value="project_admin">项目经理</Option>}
              <Option value="audit_admin">审核员</Option>
              <Option value="readonly">只读人员</Option>
            </Select>
          </Form.Item>
          <Form.Item noStyle shouldUpdate={(a, b) => a.role !== b.role}>{({ getFieldValue }) => ['audit_admin', 'readonly'].includes(getFieldValue('role')) ? <Form.Item name="project_ids" label="指派项目"><Select mode="multiple" optionFilterProp="label" options={projects.map(p => ({ value: p.id, label: p.name }))} /></Form.Item> : null}</Form.Item>
          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态！' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="active">启用</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改密码"
        open={isPasswordModalVisible}
        onOk={handlePasswordChange}
        onCancel={() => setIsPasswordModalVisible(false)}
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="oldPassword"
            label="原密码"
            rules={[{ required: true, message: '请输入原密码！' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[{ required: true, message: '请输入新密码！' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码！' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致！'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement; 